import { BACKGROUND_THEME_IDS, BackgroundThemeWrapperProps } from './constants';
import { useMemo, memo } from 'react';
import classNames from 'classnames';
import { getNavbarSizeClasses } from '../navbar/constants';
import { HippieWavesBackground } from './backgrounds/hippie-waves/HippieWavesBackground';
import { SolidBackground } from './backgrounds/SolidBackground';
import { FrostedBackground } from './backgrounds/frosted/FrostedBackground';
import { SimpleGradientBackground } from './backgrounds/simple-gradient/SimpleGradientBackground';
import { AnimatedRainbowGradient } from './backgrounds/rainbow/backgrounds/AnimatedRainbowGradient';
// import { MulligansOdyssey } from './backgrounds/geometric/backgrounds/MulligansOdyssey';
// import { TriColorTrippyPatterns } from './backgrounds/geometric/backgrounds/TriColorGeometricPatterns';
import { Hearts } from './backgrounds/rainbow/backgrounds/Hearts';
import { IntensityWrapper } from './backgrounds/IntensityWrapper';
import { Size } from '../../constants';
import { Matrix } from './backgrounds/Matrix';
import { CustomMediaBackground } from './backgrounds/CustomMediaBackground';
import { m } from 'framer-motion';
import Particles from './backgrounds/Particles';
import { ErrorBoundary } from 'react-error-boundary';
import { RippleBackgrounds } from './backgrounds/ripples/RippleBackgrounds';
import ApartmentFloorBackground from './backgrounds/ApartmentFloor';
import { GeometricBackgrounds } from './backgrounds/geometric/GeometricBackgrounds';
import { GridBackgrounds } from './backgrounds/grid/GridBackgrounds';
import { StripesBackground } from './backgrounds/stripes/StripesBackground';
import { RadialSunburst } from './backgrounds/RadialSunburst';
import WavingTransitionBackground from './backgrounds/waves/subthemes/WavingTransition';
import ElectrifyingSlopeBackground from './backgrounds/grid/subthemes/ElectrifyingSlope';
import { PresetMediaBackground } from './backgrounds/PresetMediaBackground';

// const HtmlNatesRainbowRain = lazy(() => import("./backgrounds/rainbow/rain/HtmlNatesRainbowRain"));

const BackgroundThemeWrapperComponent = (props: BackgroundThemeWrapperProps) => {
  const { themeRevision, navbarSize, topOffset, isEditing, pageId, mediaOverride } = props;
  const { contentTopPadding } = getNavbarSizeClasses(navbarSize);
  const resolvedTopOffset = topOffset ?? Number(contentTopPadding.match(/\d+/)?.[0] || '0');

  const Background = useMemo(() => {
    switch (themeRevision?.themeId) {
      case BACKGROUND_THEME_IDS.frostedGlass:
        return <FrostedBackground themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.simpleGradient:
        return <SimpleGradientBackground themeRevision={themeRevision} />;
      //   case BACKGROUND_THEME_IDS.mulligansOdyssey:
      //     return <MulligansOdyssey />;
      //   case BACKGROUND_THEME_IDS.triColorGoemetricPatterns:
      //     return <TriColorTrippyPatterns />;
      case BACKGROUND_THEME_IDS.hippieWaves:
        return <HippieWavesBackground themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.solidColor:
        return <SolidBackground themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.rainbowGradient:
        return <AnimatedRainbowGradient themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.digitalRain:
        return <Matrix themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.stripes:
        return <StripesBackground themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.particles:
        return <Particles themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.hearts:
        return <Hearts />;
      case BACKGROUND_THEME_IDS.basic:
        return <SolidBackground themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.customMedia:
        return (
          <CustomMediaBackground
            themeRevision={themeRevision}
            pageId={pageId}
            isEditing={isEditing}
            mediaOverride={mediaOverride}
          />
        );
      case BACKGROUND_THEME_IDS.presetVideoTest:
      case BACKGROUND_THEME_IDS.seasonalPresetImageTest:
      case BACKGROUND_THEME_IDS.nebulaSmoke:
      case BACKGROUND_THEME_IDS.cosmicGlass:
        return <PresetMediaBackground themeRevision={themeRevision} themeId={themeRevision?.themeId} />;
      case BACKGROUND_THEME_IDS.ripples:
        return <RippleBackgrounds themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.apartmentFloor:
        return <ApartmentFloorBackground themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.radialSunburst:
        return <RadialSunburst themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.waves:
        return <WavingTransitionBackground themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.grids:
        return <GridBackgrounds themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.geometric:
        return <GeometricBackgrounds themeRevision={themeRevision} />;
      case BACKGROUND_THEME_IDS.default:
      default:
        return <SolidBackground themeRevision={themeRevision} />;
    }
  }, [themeRevision, pageId, isEditing, mediaOverride]);

  return (
    <ErrorBoundary fallback={<div />}>
      <m.div
        animate={{ top: resolvedTopOffset }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
        className={classNames('fixed left-0 -z-10 h-full w-full')}
      >
        <IntensityWrapper
          intensity={themeRevision?.intensity}
          opacityBlur={themeRevision?.opacityBlur}
          cardCornerStyle={Size.none}
        />
        {Background}
      </m.div>
    </ErrorBoundary>
  );
};

export const BackgroundThemeWrapper = memo(BackgroundThemeWrapperComponent);
